<template>
	<div data-component="line-item" :class="{ unavailable: unavailable }">
		<div data-element="item-header">
			<p
				data-element="base-name"
				:data-wide="fullWidth"
				v-font-style="this.$store.getters['ui/getBodyCopyFontDetail']('normal')"
			>
				<span
					data-element="quantity"
					v-if="showQuantity"
				>
					{{ quantity }}x
				</span>
				{{ baseName }}
			</p>
			<p
				data-element="item-price"
				v-font-style="this.$store.getters['ui/getBodyCopyFontDetail']('normal')"
				:data-wide="fullWidth"
			>
				£{{ itemPrice | fixedPrice }}
			</p>
		</div>
		<div data-element="choices-toggle-and-quantity-increment-container">
			<div
				data-element="left-side"
				:data-wide="fullWidth"
			>
				<div
					data-element="show-choices"
					v-if="lineItem.choiceLines.length"
					@click="showChoices = !showChoices"
				>
					<span
						data-element="button-text"
						v-font-style="this.$store.getters['ui/getBodyCopyFontDetail']('normal')"
					>
						View choices
					</span>
					<icon
						data-element="show-choices-chevron"
						icon="collapse-arrow"
						size="xxs"
						:class="{ rotated: showChoices }"
						alt="Expand/collapse toggle"
					/>
				</div>
				<transition name="fade">
					<choices-modal v-if="showModal"
						:product="displayProduct"
						:portions="product.portions"
						:defaultPortionId="product.defaultPortionId"
						:choiceGroups="choiceGroups"
						:getProduct="getProduct"
						:getCG="getCG"
						:cgIndex="cgIndex"
						:addItemToBasket="addItemToBasket"
						:choiceLines="lineItem.choiceLines"
						:editBasketChoices="editBasketChoices"
						:basketIndex="basketIndex"
						edit="true"
						@close-choices-modal="closeChoicesModal"
					/>
				</transition>
			</div>
			<div
				data-element="quantity-container"
				v-if="!hideIncrementor"
			>
				<div
					data-element="remove-container"
					:data-left="showRemove"
				>
					<transition
						name="fade"
						mode="out-in"
						:duration="{ leave: 100, enter: 200}"
					>
						<div
							data-element="remove"
							v-if="showRemove"
							v-font-style="this.$store.getters['ui/getBodyCopyFontDetail']('normal')"
							:style="getRemoveStyle"
							tabindex="0"
							@click="decrementBasketItem(lineItem)"
						>
							Remove
						</div>
						<icon
							icon="remove-icon"
							data-element="decrement"
							@click.native="handleDecrementClick"
							v-else
							size="md"
							clickable="true"
							alt="Decrease quantity"
							:iconColor="this.$store.getters['ui/getSecondaryColor']"
						/>
					</transition>
				</div>
				<span
					data-element="quantity"
					v-font-style="this.$store.getters['ui/getBodyCopyFontDetail']('heavy')"
					v-modify-leading="{modifyLeading: $store.getters['ui/getBodyCopyFontLeading']}"
				>
					{{ quantity }}
				</span>
				<icon
					icon="add-icon"
					data-element="add"
					@click.native="handleIncrementClick"
					size="md"
					clickable="true"
					alt="Increase quantity"
					:iconColor="this.$store.getters['ui/getSecondaryColor']"
				/>
			</div>
		</div>
		<div
			data-element="choices"
			v-if="showChoices"
			:data-wide="fullWidth"
		>
			<div
				data-element="choice"
				v-for="(choice, index) in lineItem.choiceLines"
				:key="`choice-${index}`"
			>
				<p
					data-element="choice-name"
					v-font-style="$store.getters['ui/getBodyCopyFontDetail']('light')"
				>
					{{ getProduct(choice.ingredientId).displayRecords[0].name }}
				</p>
				<p
					data-element="choice-price"
					v-if="choicePrice(choice.ingredientId)"
					:data-wide="fullWidth"
					v-font-style="$store.getters['ui/getBodyCopyFontDetail']('light')"
				>
					£{{ choicePrice(choice.ingredientId) | fixedPrice }}
				</p>
			</div>
			<div
				data-element="edit-choices"
				v-if="!hideEditChoices && lineItem.choiceLines.length"
				@click="openChoicesModal"
				v-font-style="$store.getters['ui/getBodyCopyFontDetail']('normal')"
			>
				Edit choices
			</div>

		</div>
	</div>
</template>

<script>

	import Icon         from '@/components/ui/Icon';
	import ChoicesModal from '@/components/menu/ChoicesModal';

	export default {
		data () {
			return {
				showChoices: false,
				showModal: false,
				showRemove: false
			};
		},
		props: [
			'lineItem',
			'getProduct',
			'removeItemFromBasket',
			'addItemToBasket',
			'incrementBasketItem',
			'decrementBasketItem',
			'getCG',
			'cgIndex',
			'getBasketIndex',
			'editBasketChoices',
			'displayProduct',
			'choiceGroups',
			'hideIncrementor',
			'hideEditChoices',
			'showQuantity',
			'fullWidth',
			'unavailable'
		],
		components: {
			Icon,
			ChoicesModal
		},
		computed: {
			baseName () {
				return this.getProduct(this.lineItem.ingredientId).displayRecords[0].name
			},
			itemPrice () {
				const basePrice = this.getProduct(this.lineItem.ingredientId).portions.find(portionObject => portionObject.id === this.lineItem.portionTypeId).price;
				const choiceSum = this.lineItem.choiceLines.reduce((sum, choiceObject) => {
					const aztecChoice = this.getProduct(choiceObject.ingredientId);
					return sum + ((aztecChoice.portions && aztecChoice.portions[0] && aztecChoice.portions[0].price) || 0)
				}, 0);
				return (basePrice + choiceSum);
			},
			quantity () {
				return this.lineItem.quantity
			},
			portionDescription () {
				return this.getProduct(this.lineItem.ingredientId).portions.find(portionObject => portionObject.id === this.lineItem.portionTypeId).name;
			},
			choicesArray () {
				return this.lineItem.choiceLines;
			},
			product () {
				return this.getProduct(this.lineItem.ingredientId);
			},
			basketIndex () {
				return this.getBasketIndex(this.lineItem);
			},
			getRemoveStyle () {
				return `color: ${this.$store.getters['ui/getSecondaryColor']};`;
			}
		},
		methods: {
			priceVisible (choice) {
				return (this.getProduct(choice.ingredientId).portions[0] && this.getProduct(choice.ingredientId).portions[0].price) || 0;
			},
			choicePrice (ingredientId) {
				const aztecChoice = this.getProduct(ingredientId);
				return (aztecChoice.portions && aztecChoice.portions[0] && aztecChoice.portions[0].price) || 0;
			},
			openChoicesModal () {
				document.body.setAttribute('data-modal', true)
				document.documentElement.setAttribute('data-modal', true)
				this.showModal = true
			},
			closeChoicesModal () {
				// VueScrollTo.scrollTo(this.$parent.$refs.group, 1)
				document.body.removeAttribute('data-modal')
				document.documentElement.removeAttribute('data-modal')
				this.showModal = false
			},
			handleDecrementClick () {
				if (this.lineItem.quantity === 1 && !this.showRemove) {
					return this.showRemove = true;
				} else {
					return this.decrementBasketItem(this.lineItem);
				}
			},
			handleIncrementClick () {
				this.showRemove = false;
				this.incrementBasketItem(this.product, this.quantity, this.choicesArray);
			}
		},
		destroyed () {
			this.showRemove = false;
		}
	}

</script>

<style lang="scss" scoped>

	@import '@/assets/scss/variables.scss';
	@import '@/assets/scss/helpers.scss';

	[data-component='line-item'] {
		padding-bottom:16px;
		&.unavailable {
			border:1px solid $pinkish-red;
			background-color:#fde8e8;
			padding:2px 2px 16px 2px;
		}
		&:not(:last-child) {
			border-bottom:2px solid #9b9b9b;
		}
		[data-element='item-header'] {
			width:100%;
			display:flex;
		}
		[data-element='item-header'] {
			padding:16px 0 10px;
			[data-element='quantity'] {
				font-size: 16px;
			}
			[data-element='base-name'] {
				width:220px;
				font-size:16px;
				font-weight:500;
				&[data-wide] {
					padding-left:0;
				}
			}
			[data-element='item-price'] {
				text-align:right;
				font-size:16px;
				font-weight:500;
				flex:1;
				padding-right:3px;
				&[data-wide] {
					padding-right:0;
				}
			}
		}
		[data-element='choices-toggle-and-quantity-increment-container'] {
			display:flex;
			align-items:center;
			[data-element='left-side'] {
				flex:1;
				height:100%;
				[data-element='show-choices'] {
					display:inline-flex;
					cursor:pointer;
					[data-element='button-text'] {
						color:#000;
						font-size:14px;
						font-weight:normal;
						padding-right:4px;
					}
					[data-element='show-choices-chevron'] {
						-webkit-transition:all 0.5s ease-in-out;
						-o-transition:all 0.5s ease-in-out;
						transition:all 0.5s ease-in-out;
						margin-left:2px;
						&.rotated {
							-webkit-transform:rotate(180deg);
							-ms-transform:rotate(180deg);
							-o-transform:rotate(180deg);
							transform:rotate(180deg);
						}
					}
				}
			}
			[data-element='quantity-container'] {
				display:flex;
				flex-direction:row;
				justify-content:flex-end;
				align-items:center;
				[data-element='remove-container'] {
					display:flex;
					align-items:center;
					-webkit-tap-highlight-color:transparent;
					[data-element='remove'] {
						font-size:14px;
						font-weight:500;
						cursor:pointer;
					}
					[data-element='remove'] {
						&:focus {
							outline-color:#fff;
						}
					}
				}
				[data-element='add'] {
					right:0;
					-webkit-tap-highlight-color:transparent;
				}
				[data-element='quantity'] {
					font-size:15px;
					font-weight:500;
					color:#000;
					width:16px;
					text-align:center;
				}
			}
		}
		[data-element='choices'] {
			&[data-wide] {
				padding-left:0;
			}
			[data-element='choice'] {
				display:flex;
				padding-top:8px;
				&:first-of-type {
					padding-top:14px;
				}
				[data-element='choice-name'],
				[data-element='choice-price'] {
					font-size:16px;
					font-weight:normal;
				}
				[data-element='choice-name'] {
					width:220px;
				}
				[data-element='choice-price'] {
					flex:1;
					padding-right:3px;
					text-align:right;
					&[data-wide] {
						padding-right:0;
					}
				}
			}
			[data-element='edit-choices'] {
				margin:12px 0 4px;
				font-size:16px;
				font-weight:normal;
				text-decoration:underline;
				color:#000;
				cursor:pointer;
			}
		}
	}
</style>
